import { useMemo, useState } from 'react'
import { getApiErrorMessage, uploadAudioPhrase } from '../../api'
import { useI18n } from '../../i18n'
import { useToast } from '../../contexts/ToastContext'

function AudioRow({ item, onView }) {
  const { t } = useI18n()
  const title = item.title || item.description || item.external_id || t('media.audio.untitled')
  const voice = item.voice_id || null
  const category = item.source_kind === 'audio_phrase'
    ? t('media.audio.source.prerecorded')
    : t('media.audio.source.onlyfans')
  const duration = item.duration != null ? Number(item.duration) : null
  const durationStr =
    duration != null && duration >= 0
      ? `${Math.floor(duration / 60)}:${String(duration % 60).padStart(2, '0')}`
      : null
  const metaParts = [category]
  if (voice) metaParts.push(`${t('media.audio.voice.short')}: ${voice}`)
  if (durationStr) metaParts.push(durationStr)

  return (
    <button type="button" className="audio-library-row" onClick={() => onView?.(item)}>
      <span className="audio-library-row-icon" aria-hidden>
        <svg viewBox="0 0 24 24">
          <path d="M12 3v10.55c-.59-.34-1.27-.55-2-.55-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4V7h4V3h-6z" fill="currentColor" />
        </svg>
      </span>
      <span className="audio-library-row-main">
        <span className="audio-library-row-title">{title}</span>
        <span className="audio-library-row-meta">{metaParts.join(' / ')}</span>
      </span>
    </button>
  )
}

export default function AudioLibraryPanel({ modelId, mediaList, onView, onUploaded }) {
  const { t } = useI18n()
  const { addToast } = useToast()
  const [title, setTitle] = useState('')
  const [phraseText, setPhraseText] = useState('')
  const [voiceId, setVoiceId] = useState('')
  const [file, setFile] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const audioItems = useMemo(
    () => (mediaList || []).filter((item) => item?.type === 'audio'),
    [mediaList]
  )
  const phraseItems = audioItems.filter((item) => item.source_kind === 'audio_phrase')
  const accountItems = audioItems.filter((item) => item.source_kind !== 'audio_phrase')
  const knownVoices = Array.from(
    new Set(audioItems.map((item) => item.voice_id).filter(Boolean))
  )

  async function handleSubmit(e) {
    e.preventDefault()
    if (!modelId || !file) return
    setSaving(true)
    setError('')
    try {
      const created = await uploadAudioPhrase(modelId, file, {
        title: title.trim(),
        phrase_text: phraseText.trim(),
        voice_id: voiceId.trim(),
      })
      onUploaded?.(created)
      setTitle('')
      setPhraseText('')
      setVoiceId('')
      setFile(null)
      e.currentTarget.reset()
      addToast(t('media.audio.upload.saved'), 'success')
    } catch (err) {
      setError(getApiErrorMessage(err, t('media.audio.upload.error')))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="audio-library">
      <div className="audio-library-column">
        <div className="audio-library-heading">
          <h3>{t('media.audio.onlyfans.title')}</h3>
          <span>{accountItems.length}</span>
        </div>
        <div className="audio-library-list">
          {accountItems.length ? (
            accountItems.map((item) => (
              <AudioRow key={item.id || item.external_id} item={item} onView={onView} />
            ))
          ) : (
            <p className="audio-library-empty">{t('media.audio.onlyfans.empty')}</p>
          )}
        </div>
      </div>

      <div className="audio-library-column">
        <div className="audio-library-heading">
          <h3>{t('media.audio.phrases.title')}</h3>
          <span>{phraseItems.length}</span>
        </div>
        <form className="audio-library-upload" onSubmit={handleSubmit}>
          {error && <p className="audio-library-error">{error}</p>}
          <label>
            {t('media.audio.upload.title')}
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('media.audio.upload.titlePlaceholder')}
            />
          </label>
          <label>
            {t('media.audio.upload.text')}
            <textarea
              value={phraseText}
              onChange={(e) => setPhraseText(e.target.value)}
              placeholder={t('media.audio.upload.textPlaceholder')}
              rows={3}
            />
          </label>
          <label>
            {t('media.audio.voice.label')}
            <input
              type="text"
              list="audio-library-known-voices"
              value={voiceId}
              onChange={(e) => setVoiceId(e.target.value)}
              placeholder={t('media.audio.voice.placeholder')}
            />
            <datalist id="audio-library-known-voices">
              {knownVoices.map((value) => (
                <option key={value} value={value} />
              ))}
            </datalist>
          </label>
          <label>
            {t('media.audio.upload.file')}
            <input
              type="file"
              accept="audio/mpeg,audio/mp4,audio/wav,audio/ogg,audio/webm,audio/aac,.mp3,.m4a,.wav,.ogg,.webm,.aac"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />
          </label>
          <button type="submit" className="btn btn-primary btn-sm" disabled={saving || !file}>
            {saving ? t('common.saving') : t('media.audio.upload.submit')}
          </button>
        </form>

        <div className="audio-library-list audio-library-list--phrases">
          {phraseItems.length ? (
            phraseItems.map((item) => (
              <AudioRow key={item.id || item.external_id} item={item} onView={onView} />
            ))
          ) : (
            <p className="audio-library-empty">{t('media.audio.phrases.empty')}</p>
          )}
        </div>
      </div>
    </div>
  )
}
